import * as React from "react";
import { Card as DSCard } from "@/design-system/components";

import { cn } from "./utils";

function Card({ className, ...props }: React.ComponentProps<"div">) {
  /** Enterprise admin panels use `.enterprise-admin-panel` (dark slate). `tension-surface` sets a light `background` mix from `--card` and overrides that — causing white cards + light text. */
  const enterpriseAdmin =
    typeof className === "string" && className.includes("enterprise-admin-panel");
  /**
   * Public enterprise marketing cards define their own readable contrast via
   * `.enterprise-surface-card` / `.enterprise-deck-slide`. Avoid shared
   * `bg-card text-card-foreground tension-surface` so dark-theme card tokens
   * don't override those page-specific colors.
   */
  const enterprisePublicCard =
    typeof className === "string" &&
    (className.includes("enterprise-surface-card") ||
      className.includes("enterprise-deck-slide"));
  return (
    <DSCard
      data-slot="card"
      className={cn(
        "flex flex-col gap-6",
        enterpriseAdmin
          ? "bg-card text-foreground border-border"
          : enterprisePublicCard
            ? ""
            : "rounded-[20px] border border-[rgba(255,255,255,0.05)] bg-[rgba(17,24,39,0.6)] text-white shadow-[0_20px_40px_rgba(0,0,0,0.4)] backdrop-blur-[20px] transition-all duration-base ease-out",
        className,
      )}
      {...props}
    />
  );
}

function CardHeader({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="card-header"
      className={cn(
        "@container/card-header grid auto-rows-min grid-rows-[auto_auto] items-start gap-1.5 px-6 pt-6 has-data-[slot=card-action]:grid-cols-[1fr_auto] [.border-b]:pb-6",
        className,
      )}
      {...props}
    />
  );
}

function CardTitle({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <h4
      data-slot="card-title"
      className={cn("leading-none", className)}
      {...props}
    />
  );
}

function CardDescription({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <p
      data-slot="card-description"
      className={cn("text-muted-foreground", className)}
      {...props}
    />
  );
}

function CardAction({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="card-action"
      className={cn(
        "col-start-2 row-span-2 row-start-1 self-start justify-self-end",
        className,
      )}
      {...props}
    />
  );
}

function CardContent({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="card-content"
      className={cn("px-6 [&:last-child]:pb-6", className)}
      {...props}
    />
  );
}

function CardFooter({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="card-footer"
      className={cn("flex items-center px-6 pb-6 [.border-t]:pt-6", className)}
      {...props}
    />
  );
}

export {
  Card,
  CardHeader,
  CardFooter,
  CardTitle,
  CardAction,
  CardDescription,
  CardContent,
};
